// src/components/ErrorBoundary.tsx
//
// App-wide safety net. Without this, any render error (or a lazy chunk that
// fails to load on a flaky connection / after a new deploy) bubbles up through
// the route-level <Suspense fallback={null}> and leaves the user staring at a
// blank white screen. This catches those and shows a recoverable fallback.

import React from "react";

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  isChunkError: boolean;
  /* STATE, not an instance field. As a field, setting it in componentDidCatch
     changed nothing on screen: React had already rendered the fallback by then
     and a plain property assignment does not schedule a re-render, so whether
     the recovery message appeared depended on an unrelated re-render happening.
     A unit test caught it flapping. */
  autoReloading: boolean;
}

// A lazy() chunk that 404s after a redeploy throws a ChunkLoadError; the right
// recovery for that is a hard reload so the browser fetches the new bundle.
//
// Every engine words this differently, and the list below was originally
// Chrome/Firefox only. WebKit says "Importing a module script failed", which
// matched nothing, so iOS Safari and the iOS builds of Chrome and Edge (all
// WebKit) fell through to the generic "Something went wrong" screen with no
// recovery. That is the screen users reported: it appears once, and reloading
// by hand clears it. Most of our traffic is mobile, so most of our traffic was
// on the branch that could not heal itself.
function looksLikeChunkError(error: unknown): boolean {
  const msg = (error as any)?.message || "";
  const name = (error as any)?.name || "";
  return (
    name === "ChunkLoadError" ||
    /Loading chunk [\d]+ failed/i.test(msg) ||
    /Loading CSS chunk/i.test(msg) ||
    /dynamically imported module/i.test(msg) ||
    // WebKit / iOS
    /Importing a module script failed/i.test(msg) ||
    /Unable to preload/i.test(msg) ||
    // Generic asset fetch failures that a reload also fixes
    /Failed to fetch/i.test(msg) ||
    /NetworkError when attempting to fetch/i.test(msg)
  );
}

export default class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, isChunkError: false, autoReloading: false };
  }

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    const isChunkError = looksLikeChunkError(error);
    /* Decide here, in the same pass that renders the fallback, so the recovery
       state is on screen from the first paint rather than one render late.

       Only asset-load failures auto-recover. A genuine render bug must NOT
       cost the visitor an extra page load before it shows them a button, and
       there is a test pinning that. Widening recovery therefore means widening
       looksLikeChunkError above, not relaxing this. */
    return { hasError: true, isChunkError, autoReloading: isChunkError };
  }

  componentDidCatch(error: unknown, info: React.ErrorInfo) {
    // Log for diagnostics; a real error reporter (Sentry etc.) could hook here.
    console.error("ErrorBoundary caught:", error, info);

    // SELF-HEAL stale tabs. Every deploy renames the hashed chunks and the old
    // ones 404, so a tab opened before the deploy throws ChunkLoadError on its
    // next lazy navigation (real incident: iOS Safari tab open across several
    // same-day deploys — wallet AND subscription taps both "errored" while the
    // server never saw a request). Asking the user to press Reload is asking
    // them to fix our deploy model; do it for them. The sessionStorage stamp
    // caps it at one auto-reload per 30s so a genuinely broken build degrades
    // to the manual error screen instead of a reload loop.
    if (looksLikeChunkError(error)) {
      const KEY = "aura_chunk_reload_at";
      let last = 0;
      try { last = Number(sessionStorage.getItem(KEY) || 0); } catch { /* private mode */ }
      if (Date.now() - last > 30_000) {
        try { sessionStorage.setItem(KEY, String(Date.now())); } catch { /* ignore */ }
        /* NOT location.reload(): a reload is allowed to re-serve the SAME
           cached HTML, which asks for the same deleted chunk and fails again —
           the recovery loops instead of recovering. In-app browsers (Instagram,
           Facebook) cache HTML hardest, and they are where this hurts most.

           A changed URL cannot be answered from that cache entry, so the
           document comes from the network and carries the current chunk names.
           replace(), not assign(), so the broken page does not become a back-
           button trap. */
        try {
          const url = new URL(window.location.href);
          url.searchParams.set("_v", String(Date.now()));
          window.location.replace(url.toString());
        } catch {
          // URL unavailable (very old webview): the plain reload still helps.
          window.location.reload();
        }
      } else {
        /* Rate limited: no reload is coming, so stop promising one and show the
           recoverable error screen instead of a spinner that never resolves. */
        this.setState({ autoReloading: false });
      }
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = "/";
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    /* Auto-recovery reload is in flight.
       This used to `return null`, which paints NOTHING: on a phone the visitor
       stares at the bare page background until the reload completes, and on a
       slow connection that is several seconds of what looks like a dead site.
       Reported from the field as "a purple blank sheet on load, fine after
       reload" — this is what they were looking at.
       Say what is happening instead. */
    if (this.state.autoReloading) {
      return (
        /* Inline styles as well as the class: this renders on an error path,
           and one of the errors it recovers from is a CSS chunk that failed to
           load. The message has to be visible even with no stylesheet. */
        <div
          className="eb-updating"
          role="status"
          aria-live="polite"
          style={{
            minHeight: "70vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 16,
            padding: 24,
            textAlign: "center",
            color: "#e8e8f0",
          }}
        >
          <span className="eb-updating__spin" aria-hidden="true" />
          <p className="eb-updating__t" style={{ margin: 0, opacity: 0.85 }}>
            Updating to the latest version…
          </p>
        </div>
      );
    }

    return (
      <div
        style={{
          minHeight: "60vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          padding: "2rem",
          color: "#e8e8f0",
        }}
      >
        <div style={{ fontSize: "2.5rem", marginBottom: "0.5rem" }}>🌌</div>
        <h2 style={{ margin: "0 0 0.5rem", fontSize: "1.4rem" }}>
          {this.state.isChunkError
            ? "A new version is available"
            : "Something went wrong"}
        </h2>
        <p style={{ margin: "0 0 1.5rem", maxWidth: 420, opacity: 0.8 }}>
          {this.state.isChunkError
            ? "Please reload to get the latest version of Aura AI."
            : "An unexpected error interrupted this page. You can reload or head back home."}
        </p>
        <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", justifyContent: "center" }}>
          <button
            onClick={this.handleReload}
            style={{
              padding: "10px 22px",
              fontWeight: 600,
              color: "#fff",
              background: "linear-gradient(90deg, #FF8C42, #FF2E93)",
              border: "none",
              borderRadius: 10,
              cursor: "pointer",
            }}
          >
            Reload
          </button>
          <button
            onClick={this.handleGoHome}
            style={{
              padding: "10px 22px",
              fontWeight: 600,
              color: "#e8e8f0",
              background: "transparent",
              border: "1px solid rgba(255,255,255,0.3)",
              borderRadius: 10,
              cursor: "pointer",
            }}
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }
}
